/**
 * Détection de motifs d’erreur récurrents (Suivi élève).
 * Fonctions pures — dernières N tentatives.
 */

import {
  getBassDegreeForChord,
  PRIMARY_DEGREES,
  PARALLEL_DEGREES,
  DEGREE_TO_FUNCTIONS
} from './riemannFunctions'
import {
  parseChordDisplayString,
  getFigureValueFromParsedFigure
} from './chordFormatter'
import { revalidateStoredMarkerAnswer, isPartialValidationLevel } from './exerciseMarkerValidation'

const ROMAN_DEGREE = /^[IVX]+$/

function normalizeFigure(figure) {
  if (!figure || figure === '5') return '5'
  return String(figure).trim()
}

function normalizeDegree(raw) {
  if (!raw) return null
  const d = String(raw).trim().toUpperCase().replace(/°/g, '')
  return ROMAN_DEGREE.test(d) ? d : null
}

/** Objet accord minimal depuis une réponse stockée (objet ou QCM). */
export function chordFromStoredAnswer(answer) {
  if (!answer || typeof answer !== 'object') return null
  if (answer.degree || answer.root) {
    return {
      degree: normalizeDegree(answer.degree || answer.root),
      figure: normalizeFigure(answer.figure),
      cadence: answer.cadence || null,
      selectedFunction: answer.selectedFunction || answer.function || null
    }
  }
  const qcm = answer.qcmAnswer != null ? String(answer.qcmAnswer).trim() : ''
  if (!qcm) {
    if (answer.selectedFunction || answer.function) {
      return { degree: null, figure: '5', cadence: answer.cadence || null, selectedFunction: answer.selectedFunction || answer.function }
    }
    return null
  }
  const parsed = parseChordDisplayString(qcm)
  const figureVal = getFigureValueFromParsedFigure(parsed.figure)
  const leading = (parsed.leading || '').trim()
  const degreeMatch = leading.match(/^([IVX]+)/i)
  const degree = normalizeDegree(degreeMatch?.[1])
  return {
    degree,
    figure: normalizeFigure(figureVal),
    cadence: answer.cadence || null,
    selectedFunction: answer.function || null
  }
}

function isPrincipalParallelSwap(userDeg, correctDeg) {
  if (!userDeg || !correctDeg || userDeg === correctDeg) return false
  for (const func of ['T', 'SD', 'D']) {
    const prim = PRIMARY_DEGREES[func] || []
    const par = PARALLEL_DEGREES[func] || []
    const userPrimary = prim.includes(userDeg)
    const userParallel = par.includes(userDeg)
    const correctPrimary = prim.includes(correctDeg)
    const correctParallel = par.includes(correctDeg)
    if ((userPrimary && correctParallel) || (userParallel && correctPrimary)) return true
  }
  return false
}

function sharedFunction(userDeg, correctDeg) {
  const u = DEGREE_TO_FUNCTIONS[userDeg] || []
  const c = DEGREE_TO_FUNCTIONS[correctDeg] || []
  return u.some((f) => c.includes(f))
}

function figureToFilterTag(figure) {
  const f = normalizeFigure(figure)
  if (f === '6') return 'PremierRenversement'
  if (f === '64') return 'SecondRenversement'
  if (f === '65' || f === '43' || f === '2') return 'SeptièmePremierRenversement'
  return 'PremierRenversement'
}

function attemptDate(attempt) {
  if (!attempt?.completedAt) return null
  return attempt.completedAt.toDate ? attempt.completedAt.toDate() : new Date(attempt.completedAt)
}

export function getRecentAttempts(attempts, limit = 50) {
  return [...(attempts || [])]
    .sort((a, b) => (attemptDate(b)?.getTime() || 0) - (attemptDate(a)?.getTime() || 0))
    .slice(0, limit)
}

const EMPTY_PATTERN = () => ({
  hits: 0,
  opportunities: 0,
  examples: {}
})

/**
 * Détecte les motifs d’erreur sur les dernières tentatives.
 */
export function detectErrorPatterns(attempts, { limit = 50 } = {}) {
  const patterns = {
    bass_only: EMPTY_PATTERN(),
    degree_ok_figure_ko: EMPTY_PATTERN(),
    principal_parallel: EMPTY_PATTERN(),
    cadence_missed: EMPTY_PATTERN(),
    function_swap: EMPTY_PATTERN()
  }

  const slice = getRecentAttempts(attempts, limit)

  slice.forEach((attempt) => {
    const correctAnswers = attempt.correctAnswers || []
    const userAnswers = attempt.userAnswers || []
    correctAnswers.forEach((correct, index) => {
      if (!correct) return
      const userAnswer = userAnswers[index]
      if (!userAnswer) return

      const userChord = chordFromStoredAnswer(userAnswer)
      const correctChord = chordFromStoredAnswer(correct) || {
        degree: normalizeDegree(correct.degree),
        figure: normalizeFigure(correct.figure),
        cadence: correct.cadence || null
      }
      if (!userChord?.degree || !correctChord?.degree) return

      const validation = revalidateStoredMarkerAnswer(userAnswer, correct, {
        index,
        progressionMode: attempt.progressionMode,
        tonality: attempt.tonality,
        mode: attempt.mode || attempt.dominantMode
      })
      if (validation.level === 1) return

      const userDeg = userChord.degree
      const correctDeg = correctChord.degree
      const userFig = userChord.figure || '5'
      const correctFig = correctChord.figure || '5'

      const correctBass = getBassDegreeForChord(correctDeg, correctFig)
      const isBassOnly =
        correctFig === '6' &&
        (userFig === '5' || !userChord.figure) &&
        userDeg === correctBass &&
        userDeg !== correctDeg

      if (isBassOnly) {
        patterns.bass_only.opportunities++
        patterns.bass_only.hits++
        const key = `${userDeg}→${correctDeg}${correctFig !== '5' ? correctFig : '6'}`
        patterns.bass_only.examples[key] = (patterns.bass_only.examples[key] || 0) + 1
        return
      }

      if (userDeg === correctDeg && userFig !== correctFig) {
        patterns.degree_ok_figure_ko.opportunities++
        patterns.degree_ok_figure_ko.hits++
        const key = `${correctDeg}${correctFig}`
        patterns.degree_ok_figure_ko.examples[key] = (patterns.degree_ok_figure_ko.examples[key] || 0) + 1
      }

      if (userDeg !== correctDeg && isPrincipalParallelSwap(userDeg, correctDeg)) {
        patterns.principal_parallel.opportunities++
        patterns.principal_parallel.hits++
        const key = `${userDeg}→${correctDeg}`
        patterns.principal_parallel.examples[key] = (patterns.principal_parallel.examples[key] || 0) + 1
      }

      const correctCadence = correct.cadence || correctChord.cadence
      if (correctCadence) {
        const degreeOk =
          userDeg === correctDeg ||
          isPartialValidationLevel(validation.level) ||
          (validation.score ?? 0) >= 65
        if (degreeOk) {
          patterns.cadence_missed.opportunities++
          const userCad = userChord.cadence || userAnswer.cadence
          if (!userCad || userCad !== correctCadence) {
            patterns.cadence_missed.hits++
            patterns.cadence_missed.examples[correctCadence] =
              (patterns.cadence_missed.examples[correctCadence] || 0) + 1
          }
        }
      }

      if (userDeg !== correctDeg && sharedFunction(userDeg, correctDeg) && !isPrincipalParallelSwap(userDeg, correctDeg)) {
        patterns.function_swap.opportunities++
        patterns.function_swap.hits++
      }
    })
  })

  if (patterns.bass_only.opportunities > 0) {
    patterns.degree_ok_figure_ko.hits = 0
    patterns.degree_ok_figure_ko.opportunities = 0
  }

  return patterns
}

function topExample(examples) {
  const entries = Object.entries(examples || {})
  if (!entries.length) return null
  entries.sort((a, b) => b[1] - a[1])
  return entries[0][0]
}

function patternErrorRate(stat) {
  if (!stat?.opportunities) return 100
  return Math.round((stat.hits / stat.opportunities) * 100)
}

/** Seuils motif : ≥3 hits et ≥50 % sur les occasions du motif. */
function patternMeetsThreshold(stat) {
  return stat.hits >= 3 && stat.opportunities > 0 && stat.hits / stat.opportunities >= 0.5
}

/**
 * Convertit les motifs détectés en points faibles actionnables (shape unifiée).
 */
export function patternsToWeakPoints(patterns) {
  if (!patterns) return []
  const out = []

  if (patternMeetsThreshold(patterns.bass_only)) {
    const ex = topExample(patterns.bass_only.examples)
    const rate = patternErrorRate(patterns.bass_only)
    out.push({
      type: 'pattern',
      value: 'bass_only',
      name: 'Renversements (basse)',
      label: 'Renversements (basse)',
      successRate: Math.max(0, 100 - rate),
      percentage: Math.max(0, 100 - rate),
      filterTag: 'PremierRenversement',
      practiceLabel: '1er renversement',
      detail: ex
        ? `Confusion basse / accord (ex. ${ex.replace('→', ' pour ')}).`
        : 'Confusion basse / accord.',
      codex: { ficheId: 'fiche-renversements', sectionId: null },
      priority: 1,
      evidence: { n: patterns.bass_only.hits, example: ex }
    })
  }

  if (patternMeetsThreshold(patterns.degree_ok_figure_ko)) {
    const ex = topExample(patterns.degree_ok_figure_ko.examples)
    const figHint = ex?.match(/\d+$/)?.[0] || '6'
    const tag = figureToFilterTag(figHint)
    const rate = patternErrorRate(patterns.degree_ok_figure_ko)
    out.push({
      type: 'pattern',
      value: 'degree_ok_figure_ko',
      name: 'Renversements',
      label: 'Renversements',
      successRate: Math.max(0, 100 - rate),
      percentage: Math.max(0, 100 - rate),
      filterTag: tag,
      practiceLabel: tag === 'SecondRenversement' ? '2e renversement' : '1er renversement',
      detail: 'Bon degré, mauvais renversement.',
      codex: { ficheId: 'fiche-renversements', sectionId: null },
      priority: 1,
      evidence: { n: patterns.degree_ok_figure_ko.hits, example: ex }
    })
  }

  if (patternMeetsThreshold(patterns.principal_parallel)) {
    const ex = topExample(patterns.principal_parallel.examples)
    const rate = patternErrorRate(patterns.principal_parallel)
    let fnTag = 'Tonique'
    if (ex?.startsWith('VII') || ex?.endsWith('V')) fnTag = 'Dominante'
    else if (ex?.startsWith('II') || ex?.endsWith('IV')) fnTag = 'SousDominante'
    out.push({
      type: 'pattern',
      value: 'principal_parallel',
      name: 'Degrés principaux / parallèles',
      label: 'Degrés principaux / parallèles',
      suggestionsSubtitle: "Pour t'entraîner sur les degrés principaux et parallèles.",
      successRate: Math.max(0, 100 - rate),
      percentage: Math.max(0, 100 - rate),
      filterTag: fnTag,
      practiceLabel: fnTag === 'Dominante' ? 'la dominante' : fnTag === 'SousDominante' ? 'la sous-dominante' : 'la tonique',
      detail: ex
        ? `Bonne fonction, mauvais degré (ex. ${ex.replace('→', ' pour ')}).`
        : 'Bonne fonction, mauvais degré.',
      codex: { ficheId: 'fiche-fonctions-tsd-d', sectionId: null },
      priority: 2,
      evidence: { n: patterns.principal_parallel.hits, example: ex }
    })
  }

  if (patternMeetsThreshold(patterns.cadence_missed)) {
    const cadenceKey = topExample(patterns.cadence_missed.examples)
    const rate = patternErrorRate(patterns.cadence_missed)
    const CADENCE_TO_TAG = {
      perfect: 'CadenceParfaite',
      imperfect: 'CadenceImparfaite',
      plagal: 'CadencePlagale',
      deceptive: 'CadenceRompue',
      half: 'DemiCadence',
      'demi-cadence': 'DemiCadence'
    }
    const filterTag = CADENCE_TO_TAG[cadenceKey] || 'CadenceParfaite'
    out.push({
      type: 'pattern',
      value: 'cadence_missed',
      name: 'Cadences',
      label: 'Cadences',
      successRate: Math.max(0, 100 - rate),
      percentage: Math.max(0, 100 - rate),
      filterTag,
      practiceLabel: 'les cadences',
      detail: 'Accord correct, cadence à revoir.',
      codex: { ficheId: 'fiche-demi-cadence-parfaite', sectionId: null },
      priority: 2,
      evidence: { n: patterns.cadence_missed.hits, example: cadenceKey }
    })
  }

  if (patternMeetsThreshold(patterns.function_swap)) {
    const rate = patternErrorRate(patterns.function_swap)
    out.push({
      type: 'pattern',
      value: 'function_swap',
      name: 'Fonctions',
      label: 'Fonctions',
      successRate: Math.max(0, 100 - rate),
      percentage: Math.max(0, 100 - rate),
      filterTag: 'Tonique',
      practiceLabel: 'les fonctions',
      detail: 'Confusion entre tonique, sous-dominante et dominante.',
      codex: { ficheId: 'fiche-fonctions-tsd-d', sectionId: null },
      priority: 3,
      evidence: { n: patterns.function_swap.hits }
    })
  }

  return out
}
